import React from 'react';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ArticleContent from './components/ArticleContent';
import SkillsAssessmentTool from './components/SkillsAssessmentTool';
import CompetencyFramework from './components/CompetencyFramework';
import TrainingProgramsPreview from './components/TrainingProgramsPreview';
import SuccessStories from './components/SuccessStories';
import RelatedArticles from './components/RelatedArticles';
import CTASection from './components/CTASection';
import Image from '../../components/AppImage';

const SkillsDevelopmentInDigitalEraArticle = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Main Content */}
      <main className="pt-16">
        <HeroSection />
        <ArticleContent />
        <SkillsAssessmentTool />
        <CompetencyFramework />
        <TrainingProgramsPreview />
        <SuccessStories />
        <RelatedArticles />
        <CTASection />
      </main>
      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2 space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center">
                  <Image
                    src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                    alt="CRF PERFECTION logo featuring interlocking heart-shaped design in red and gold colors representing Centre de Recherche et de Formation business therapy excellence"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">CRF PERFECTION</h3>
                  <p className="text-blue-200 font-semibold">Centre de Recherche et de Formation</p>
                  <p className="text-gray-300 text-sm">Expert en Développement des Compétences</p>
                </div>
              </div>
              <p className="text-blue-100 mb-6 leading-relaxed max-w-2xl">
                Découvrez comment transformer vos compétences pour exceller dans l'ère numérique. Notre expertise en développement professionnel guide votre organisation vers l'excellence opérationnelle.
              </p>
            </div>

            {/* Article Topics */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold mb-4 text-white">Sujets Clés</h4>
              <ul className="space-y-3 text-blue-100">
                <li><a href="#skills-assessment" className="hover:text-white transition-colors duration-200 text-sm flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  <span>Évaluation des Compétences</span>
                </a></li>
                <li><a href="#competency-framework" className="hover:text-white transition-colors duration-200 text-sm flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>Référentiel de Compétences</span>
                </a></li>
                <li><a href="#training-programs" className="hover:text-white transition-colors duration-200 text-sm flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  <span>Programmes de Formation</span>
                </a></li>
                <li><a href="#success-stories" className="hover:text-white transition-colors duration-200 text-sm flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                  <span>Témoignages de Réussite</span>
                </a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold mb-4 text-white">Accompagnement</h4>
              <div className="space-y-4 text-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <span className="text-sm">Évaluation Gratuite</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <span className="text-sm">Programmes Personnalisés</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-blue-400/20 mt-12 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
              <div className="text-center lg:text-left">
                <p className="text-blue-200 text-sm">
                  © {new Date()?.getFullYear()} CRF PERFECTION - Développement des Compétences à l'Ère Numérique.
                </p>
              </div>
              <div className="flex flex-wrap justify-center lg:justify-end space-x-6">
                <a href="/blog-hub" className="text-blue-200 hover:text-white text-sm transition-colors duration-200">
                  Retour au Blog
                </a>
                <a href="/contact" className="text-blue-200 hover:text-white text-sm transition-colors duration-200">
                  Contact Expert
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SkillsDevelopmentInDigitalEraArticle;